'use client'

import { useEffect } from 'react'
import { reportClientError } from '@colix/frontend-logger'
import { Button } from '@/components/ui/button'
import { logger } from '@/lib/logger'

/**
 * Next.js page-level error boundary — catches errors in page components.
 * The root layout remains intact so nav/sidebar still renders.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    reportClientError(
      logger,
      error,
      {
        component: 'PageErrorBoundary',
        action: 'page_crash',
        error_digest: error.digest,
        page_url: window.location.href,
        error_category:
          error.message.includes('#418') || error.message.includes('#422')
            ? 'hydration'
            : undefined,
      },
      {
        reporter: 'next-page-boundary',
      }
    )
  }, [error])

  // In production, Next.js strips real error messages to a generic string.
  // Show the digest so it can be correlated with server logs.
  const isGeneric =
    error.message.includes('Server Components') || error.message.includes('Minified React')
  const displayMessage = isGeneric ? 'Something went wrong loading this page.' : error.message

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <div className="max-w-md w-full text-center">
        <h2 className="text-xl font-semibold text-foreground mb-3">Page error</h2>
        <p className="text-muted-foreground mb-6 text-sm">{displayMessage}</p>
        {error.digest && (
          <p className="mb-4 font-mono text-xs text-muted-foreground">{error.digest}</p>
        )}
        <Button onClick={reset}>Try again</Button>
      </div>
    </div>
  )
}
